/**
 * Central Axios instance.
 *
 * - Attaches the current access token to every outgoing request.
 * - On a 401 response, attempts a single silent refresh-token exchange and
 *   retries the original request; if the refresh itself fails, the user is
 *   logged out and redirected to "/" — the public landing page (see
 *   `pages/landing/LandingPage.tsx`), which now hosts the login modal.
 */

import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import type { TokenPair } from "@/types";

const ACCESS_TOKEN_KEY = "tekinvest_access_token";
const REFRESH_TOKEN_KEY = "tekinvest_refresh_token";

export const tokenStorage = {
  getAccessToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (tokens: Pick<TokenPair, "access_token" | "refresh_token">): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  },
  clear: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

const baseURL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";

export const api = axios.create({ baseURL });

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function resolvePendingQueue(token: string | null) {
  pendingQueue.forEach((resolve) => resolve(token));
  pendingQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken || originalRequest.url?.includes("/auth/")) {
      tokenStorage.clear();
      window.location.assign("/");
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push((newToken) => {
          if (!newToken) {
            reject(error);
            return;
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post<TokenPair>(`${baseURL}/auth/refresh-token`, {
        refresh_token: refreshToken,
      });
      tokenStorage.setTokens(data);
      resolvePendingQueue(data.access_token);
      originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      return api(originalRequest);
    } catch (refreshError) {
      resolvePendingQueue(null);
      tokenStorage.clear();
      window.location.assign("/");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
